import { useMutation, useQueryClient } from '@tanstack/react-query';
import StatsService, {
  type UrinationPatternFileFormat,
} from '../../services/StatsService';
import { showGlobalToast } from '../../utils/toastHelper';
import i18n from '../../i18n';
import { useManifest } from '../../context/ManifestContext';
import { generateLangForPayload } from '../../utils/generateLangForPayload';

export type UrinationPatternExportVariables = {
  carehomeId: string;
  format: UrinationPatternFileFormat;
  residentIds: string[];
};

/** Shared across callers so AnalysisPage can use useIsMutating for the overlay loader. */
export const URINATION_PATTERN_EXPORT_MUTATION_KEY = [
  'urinationPatternExport',
] as const;

export function useUrinationPatternExportMutation() {
  const { languages } = useManifest();

  return useMutation({
    mutationKey: URINATION_PATTERN_EXPORT_MUTATION_KEY,
    mutationFn: async (
      variables: UrinationPatternExportVariables
    ): Promise<void> =>
      StatsService.exportUrinationPattern({
        carehomeId: variables.carehomeId,
        fileFormat: variables.format,
        residentIds: variables.residentIds,
        language: generateLangForPayload(i18n.language, languages),
      }),
    onSuccess: () => {
      showGlobalToast(
        i18n.t('success'),
        i18n.t('general.exportQueuedByEmail'),
        'success'
      );
    },
  });
}

export type RepositioningHistoryExportVariables = {
  carehomeId: string;
  residentIds: string[];
  startDateIso: string;
  endDateIso: string;
};

export const REPOSITIONING_HISTORY_EXPORT_MUTATION_KEY = [
  'repositioningHistoryExport',
] as const;

export function useRepositioningHistoryExportMutation() {
  const { languages } = useManifest();

  return useMutation({
    mutationKey: REPOSITIONING_HISTORY_EXPORT_MUTATION_KEY,
    mutationFn: async (
      variables: RepositioningHistoryExportVariables
    ): Promise<void> =>
      StatsService.exportRepositioningHistory({
        carehomeId: variables.carehomeId,
        residentIds: variables.residentIds,
        startDateIso: variables.startDateIso,
        endDateIso: variables.endDateIso,
        language: generateLangForPayload(i18n.language, languages),
      }),
    onSuccess: () => {
      showGlobalToast(
        i18n.t('success'),
        i18n.t('general.exportQueuedByEmail'),
        'success'
      );
    },
  });
}

export type UrineHistoryExportVariables = {
  carehomeId: string;
  residentIds: string[];
  startDateIso: string;
  endDateIso: string;
};

export const URINE_HISTORY_EXPORT_MUTATION_KEY = [
  'urineHistoryExport',
] as const;

export function useUrineHistoryExportMutation() {
  const { languages } = useManifest();

  return useMutation({
    mutationKey: URINE_HISTORY_EXPORT_MUTATION_KEY,
    mutationFn: async (
      variables: UrineHistoryExportVariables
    ): Promise<void> =>
      StatsService.exportUrineHistory({
        carehomeId: variables.carehomeId,
        residentIds: variables.residentIds,
        startDateIso: variables.startDateIso,
        endDateIso: variables.endDateIso,
        language: generateLangForPayload(i18n.language, languages),
      }),
    onSuccess: () => {
      showGlobalToast(
        i18n.t('success'),
        i18n.t('general.exportQueuedByEmail'),
        'success'
      );
    },
  });
}

export function useAnalysisPageMutations() {
  const queryClient = useQueryClient();

  const invalidateAnalysisCareHomes = async () => {
    await queryClient.invalidateQueries({ queryKey: ['analysisCareHomes'] });
  };

  const invalidateAnalysisStations = async () => {
    await queryClient.invalidateQueries({ queryKey: ['analysisStations'] });
  };

  const invalidateAnalysisResidents = async () => {
    await queryClient.invalidateQueries({ queryKey: ['analysisResidents'] });
  };

  const invalidateAnalysisResidentName = async () => {
    await queryClient.invalidateQueries({ queryKey: ['analysisResidentName'] });
  };

  const invalidateAnalysisDiapersStat = async () => {
    await queryClient.invalidateQueries({ queryKey: ['analysisDiapersStat'] });
  };

  const invalidateAnalysisUrineLevelDistribution = async () => {
    await queryClient.invalidateQueries({
      queryKey: ['analysisUrineLevelDistribution'],
    });
  };

  const invalidateAnalysisUrineLevelDistributionByTime = async () => {
    await queryClient.invalidateQueries({
      queryKey: ['analysisUrineLevelDistributionByTime'],
    });
  };

  const invalidateAllAnalysis = async () => {
    await Promise.all([
      invalidateAnalysisCareHomes(),
      invalidateAnalysisStations(),
      invalidateAnalysisResidents(),
      invalidateAnalysisResidentName(),
      invalidateAnalysisDiapersStat(),
      invalidateAnalysisUrineLevelDistribution(),
      invalidateAnalysisUrineLevelDistributionByTime(),
    ]);
  };

  return {
    invalidateAllAnalysis,
    invalidateAnalysisCareHomes,
    invalidateAnalysisStations,
    invalidateAnalysisResidents,
    invalidateAnalysisResidentName,
    invalidateAnalysisDiapersStat,
    invalidateAnalysisUrineLevelDistribution,
    invalidateAnalysisUrineLevelDistributionByTime,
  };
}
